import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { companyCountryAlpha2 } from '../constants/countries';
import { useAuth } from './AuthContext';

const ProductsContext = createContext();

function packagingApiBaseUrl() {
  return (process.env.REACT_APP_PACKAGING_API_URL || '').replace(/\/+$/, '');
}

/** Build URL for packaging items/boxes. `resourcePath` is like `/items/LV` or `/boxes/LV`. */
function packagingResourceUrl(resourcePath) {
  const path = resourcePath.startsWith('/') ? resourcePath : `/${resourcePath}`;
  const base = packagingApiBaseUrl();
  if (base) {
    if (base.endsWith('/v1/packaging')) {
      return `${base}${path}`;
    }
    return `${base}/v1/packaging${path}`;
  }
  return new URL(`/v1/packaging${path}`, window.location.origin).toString();
}

function toNumberOrNull(value) {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizePackerItem(item, index) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const sku = item.sku ?? item.SKU ?? item.Article ?? item.article ?? '';
  const skuText = String(sku).trim();
  if (!skuText) {
    return null;
  }

  const name = item.name ?? item.Name ?? null;
  const length_cm = toNumberOrNull(
    item.length_cm ?? item.length ?? item.Length ?? item.inner_l,
  );
  const width_cm = toNumberOrNull(
    item.width_cm ?? item.width ?? item.Width ?? item.inner_w,
  );
  const height_cm = toNumberOrNull(
    item.height_cm ?? item.height ?? item.Height ?? item.inner_h,
  );
  const weight_kg = toNumberOrNull(
    item.weight_kg ?? item.weight ?? item.Weight,
  );

  let shape = item.shape ?? item.Shape ?? null;
  if (shape == null && item.cylinder === true) {
    shape = 'cylinder';
  }
  if (shape == null) {
    shape = 'box';
  }

  return {
    id: skuText,
    sku: skuText,
    name: name != null ? String(name) : null,
    length_cm,
    width_cm,
    height_cm,
    weight_kg,
    shape: String(shape).toLowerCase(),
    raw: item,
  };
}

function normalizePackerBox(item, index) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const name = item.name ?? item.Name ?? item.box_code ?? item.code ?? '';
  const nameText = String(name).trim();
  if (!nameText) {
    return null;
  }

  return {
    id: nameText,
    name: nameText,
    inner_length_cm: toNumberOrNull(item.inner_l ?? item.inner_length_cm),
    inner_width_cm: toNumberOrNull(item.inner_w ?? item.inner_width_cm),
    inner_height_cm: toNumberOrNull(item.inner_h ?? item.inner_height_cm),
    outer_length_cm: toNumberOrNull(
      item.outer_l ?? item.length ?? item.length_cm ?? item.outer_length_cm,
    ),
    outer_width_cm: toNumberOrNull(
      item.outer_w ?? item.width ?? item.width_cm ?? item.outer_width_cm,
    ),
    outer_height_cm: toNumberOrNull(
      item.outer_h ?? item.height ?? item.height_cm ?? item.outer_height_cm,
    ),
    volume: toNumberOrNull(item.volume ?? item.volume_cm3),
    raw: item,
  };
}

function normalizeListResponse(data, normalizeRow) {
  const rows = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
  return rows.map((item, index) => normalizeRow(item, index)).filter(Boolean);
}

export function ProductsProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [error, setError] = useState(null);
  const [boxesError, setBoxesError] = useState(null);
  const refreshInFlightRef = useRef(false);
  const productsRef = useRef(products);
  const boxesRef = useRef(boxes);

  productsRef.current = products;
  boxesRef.current = boxes;

  const countryCode = useMemo(() => {
    const company = user?.activeCompany || user?.company || user?.myCompany || null;
    return companyCountryAlpha2(company);
  }, [user]);

  const refreshProducts = useCallback(async () => {
    if (!countryCode) {
      setProducts([]);
      setBoxes([]);
      setError('packagingSelectCountry');
      setBoxesError('packagingSelectCountry');
      setLoading(false);
      return { products: [], boxes: [] };
    }

    if (refreshInFlightRef.current) {
      return { products: productsRef.current, boxes: boxesRef.current };
    }

    refreshInFlightRef.current = true;

    try {
      setLoading(true);
      setError(null);
      setBoxesError(null);

      const code = encodeURIComponent(countryCode);
      const [itemsRes, boxesRes] = await Promise.all([
        fetch(packagingResourceUrl(`/items/${code}`)),
        fetch(packagingResourceUrl(`/boxes/${code}`)),
      ]);

      let nextProducts = [];
      let nextBoxes = [];

      if (itemsRes.ok) {
        const data = await itemsRes.json();
        nextProducts = normalizeListResponse(data, normalizePackerItem);
        setProducts(nextProducts);
      } else {
        setProducts([]);
        setError('productsLoadFailed');
      }

      if (boxesRes.ok) {
        const data = await boxesRes.json();
        nextBoxes = normalizeListResponse(data, normalizePackerBox);
        setBoxes(nextBoxes);
      } else {
        setBoxes([]);
        setBoxesError('boxesLoadFailed');
      }

      return { products: nextProducts, boxes: nextBoxes };
    } catch (err) {
      console.error('Failed to load packaging data:', err);
      setProducts([]);
      setBoxes([]);
      setError('productsLoadFailed');
      setBoxesError('boxesLoadFailed');
      return { products: [], boxes: [] };
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, [countryCode]);

  return (
    <ProductsContext.Provider
      value={{
        products,
        boxes,
        countryCode,
        refreshProducts,
        loading,
        error,
        boxesError,
        setError,
        setBoxesError,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  return useContext(ProductsContext);
}
